// Zod DTOs for the `/v1/assets` R2 asset service (P2-03, spec §7.1 "Upload
// flow" + §7.2 "Delivery"). Three requests, mirroring the wire contract
// verbatim:
//
//   POST /v1/assets/upload-url    { filename, mimeType, sizeBytes, kind }
//     -> { assetId, url, headers, expiresIn: 900 }, Asset row = PENDING
//   POST /v1/assets/:id/finalise  { checksumSha256 }
//     -> the finalised Asset (READY), or 422 ASSET_TOO_LARGE/ASSET_MIME_REJECTED
//   POST /v1/assets/:id/download-url  { disposition? }
//     -> { url, expiresIn } — a short-lived (<=300s) signed GET
//
// `kind` (§7.1's MIME-allowlist/size-cap selector) is request-only — it is
// never a column on `Asset` (see `../enums.ts`'s `AssetKind` header for why),
// so there is no corresponding field on the response schema below.
import { z } from 'zod';
import { AssetKind, AssetStatus } from '../enums';

const assetKindSchema = z.enum(Object.values(AssetKind) as [AssetKind, ...AssetKind[]]);
const assetStatusSchema = z.enum(Object.values(AssetStatus) as [AssetStatus, ...AssetStatus[]]);

/** Bounded free-text — the client's own filename, used ONLY to inform the
 * `response-content-disposition` filename on download; never used to derive
 * the (opaque, server-chosen) `storageKey` extension — see
 * `apps/api/src/services/r2.ts`'s `extensionForMimeType`. */
const filenameSchema = z.string().trim().min(1, 'filename is required').max(255);

/** A caller-declared size, in bytes — untrusted (spec §7.1: finalise
 * re-verifies the REAL object size via `HeadObject`); only used here as a
 * fail-fast pre-check against the declared `kind`'s cap before a presigned
 * URL is even minted. `z.coerce` NOT used — this is a JSON body field, not a
 * query string, so it arrives as a real number already. */
const sizeBytesSchema = z.number().int().positive();

// ─── POST /v1/assets/upload-url ─────────────────────────────────────────────

export const assetUploadUrlRequestSchema = z.object({
  filename: filenameSchema,
  mimeType: z.string().trim().min(1, 'mimeType is required').max(255),
  sizeBytes: sizeBytesSchema,
  kind: assetKindSchema,
});
export type AssetUploadUrlRequest = z.infer<typeof assetUploadUrlRequestSchema>;

export const assetUploadUrlResponseSchema = z.object({
  assetId: z.string(),
  url: z.string(),
  /** Headers the client MUST send with its direct `PUT` to `url` — always
   * exactly `{ "Content-Type": <the same mimeType submitted above> }`, since
   * Content-Type is pinned into the SigV4 signature (spec §7.1 platform
   * note) and R2 will reject a mismatched header with a signature error. */
  headers: z.record(z.string(), z.string()),
  expiresIn: z.number().int().positive(),
});
export type AssetUploadUrlResponse = z.infer<typeof assetUploadUrlResponseSchema>;

// ─── POST /v1/assets/:id/finalise ───────────────────────────────────────────

/** A lowercase-hex SHA-256 digest the CLIENT computed over the uploaded
 * bytes — recorded verbatim on `Asset.checksumSha256` for future integrity
 * comparisons. Deliberately NOT recomputed/verified server-side against the
 * real object bytes here: hashing a file up to the VIDEO cap (2 GB) inline
 * on the request path would be exactly the "CPU-heavy... never inline"
 * derivation work spec §7.1/§13 reserves for an async Queue consumer, and
 * finalise's own security boundary is the magic-byte sniff + MIME allowlist
 * + size cap below, not checksum equality. */
const checksumSha256Schema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[0-9a-f]{64}$/, 'checksumSha256 must be a 64-character lowercase hex SHA-256 digest');

export const assetFinaliseRequestSchema = z.object({
  checksumSha256: checksumSha256Schema.optional(),
});
export type AssetFinaliseRequest = z.infer<typeof assetFinaliseRequestSchema>;

// ─── Asset response (finalise's 200, and any future GET) ───────────────────
// `sizeBytes` is `BigInt` on the Prisma row (`Asset.sizeBytes`, SQLite/D1
// INTEGER is 64-bit) — `BigInt` does not survive `JSON.stringify`/zod
// natively, so the wire shape carries it as a decimal STRING (the same
// "guard JS-side (de)serialization... coerce explicitly" the schema's own
// comment calls for), never a raw `number` (would lose precision above
// 2^53-1, plausible for a 2 GB video).

export const assetResponseSchema = z.object({
  id: z.string(),
  bucket: z.string(),
  storageKey: z.string(),
  originalName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.string(),
  checksumSha256: z.string().nullable(),
  status: assetStatusSchema,
  uploadedById: z.string(),
  createdAt: z.string().datetime(),
});
export type AssetResponse = z.infer<typeof assetResponseSchema>;

// ─── POST /v1/assets/:id/download-url ───────────────────────────────────────

export const assetDownloadUrlRequestSchema = z.object({
  /** `response-content-disposition` (spec §7.2: "inline (preview-only tabs)
   * or attachment (downloadable)"), caller-supplied since a tab's
   * `allowDownload` flag lives one layer up (`ResourceTab`, not `Asset`) and
   * this service has no tab context of its own. Defaults to the more
   * conservative `inline` (preview without triggering a save-as prompt). */
  disposition: z.enum(['inline', 'attachment']).default('inline'),
});
export type AssetDownloadUrlRequest = z.infer<typeof assetDownloadUrlRequestSchema>;

export const assetDownloadUrlResponseSchema = z.object({
  url: z.string(),
  /** Always <= 300 (spec §7.2 "TTL 300 s"). */
  expiresIn: z.number().int().positive(),
});
export type AssetDownloadUrlResponse = z.infer<typeof assetDownloadUrlResponseSchema>;
